import { useCallback, useEffect, useRef } from 'react';

const STICK_TO_BOTTOM_THRESHOLD_PX = 32;

export function useV3StickyScroll({
    entryCount,
    isBusy,
    userMessageCount,
}: {
    readonly entryCount: number;
    readonly isBusy: boolean;
    readonly userMessageCount: number;
}) {
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const shouldStickRef = useRef(true);
    const lastObservedScrollTopRef = useRef<number | null>(null);
    const previousUserMessageCountRef = useRef(userMessageCount);
    const getViewport = useCallback(
        () =>
            scrollAreaRef.current?.querySelector<HTMLElement>(
                '[data-slot="scroll-area-viewport"]',
            ) ?? null,
        [],
    );
    const scrollToBottom = useCallback(() => {
        const viewport = getViewport();
        if (viewport === null) {
            return;
        }
        viewport.scrollTop = viewport.scrollHeight;
        lastObservedScrollTopRef.current = viewport.scrollTop;
    }, [getViewport]);

    useEffect(() => {
        const previousUserMessageCount = previousUserMessageCountRef.current;
        previousUserMessageCountRef.current = userMessageCount;
        if (userMessageCount <= previousUserMessageCount) {
            return;
        }
        // Message IDs change while a send is saved. The count grows only for
        // a new turn, including an answer that replaces a question card.
        shouldStickRef.current = true;
        scrollToBottom();
    }, [scrollToBottom, userMessageCount]);

    useEffect(() => {
        const viewport = getViewport();
        if (viewport === null) {
            return;
        }
        const activeViewport = viewport;
        lastObservedScrollTopRef.current = activeViewport.scrollTop;
        function isNearBottom() {
            const distanceToBottom =
                activeViewport.scrollHeight -
                activeViewport.scrollTop -
                activeViewport.clientHeight;
            return distanceToBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
        }
        function updateStickiness() {
            const movedTowardBottom =
                lastObservedScrollTopRef.current !== null &&
                activeViewport.scrollTop > lastObservedScrollTopRef.current;

            // A content-height change can clamp scrollTop and emit a scroll
            // event. That is not a request from a detached reader to resume
            // following the response.
            shouldStickRef.current =
                isNearBottom() && (shouldStickRef.current || movedTowardBottom);
            lastObservedScrollTopRef.current = activeViewport.scrollTop;
        }
        function updateStickinessOnWheel(event: WheelEvent) {
            if (event.deltaY < 0) {
                shouldStickRef.current = false;
            } else if (event.deltaY > 0 && isNearBottom()) {
                // A shrink-clamped viewport cannot move farther down, but a
                // downward wheel still signals an intent to resume following.
                shouldStickRef.current = true;
            }
        }
        viewport.addEventListener('scroll', updateStickiness, {
            passive: true,
        });
        viewport.addEventListener('wheel', updateStickinessOnWheel, {
            passive: true,
        });
        return () => {
            viewport.removeEventListener('scroll', updateStickiness);
            viewport.removeEventListener('wheel', updateStickinessOnWheel);
        };
    }, [getViewport]);

    useEffect(() => {
        if (shouldStickRef.current) {
            scrollToBottom();
        }
    }, [entryCount, scrollToBottom]);

    useEffect(() => {
        const content = contentRef.current;
        if (content === null || typeof ResizeObserver === 'undefined') {
            return;
        }
        let frameId: number | null = null;
        const followResize = () => {
            if (!shouldStickRef.current || frameId !== null) {
                return;
            }
            frameId = window.requestAnimationFrame(() => {
                frameId = null;
                if (shouldStickRef.current) {
                    scrollToBottom();
                }
            });
        };
        const observer = new ResizeObserver(followResize);
        observer.observe(content);
        followResize();
        return () => {
            observer.disconnect();
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [isBusy, scrollToBottom]);

    return { scrollAreaRef, contentRef };
}
